import React, { useState, useEffect } from 'react';

import {
  FiInfo,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiActivity,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiFileText,
  FiX,
  FiCheck,
  FiEdit,
  FiNavigation,
  FiGlobe,
  FiPercent
} from 'react-icons/fi';
import { FaRupeeSign, FaDownload } from 'react-icons/fa';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../libs/axios';
import { useAuth } from '../../../provider/auth.provider';
import validator from 'validator';
import { formatDate } from '../../activities/edit';
import { LuActivity } from 'react-icons/lu';
import EntityLogs from '../../../utils/logs.utils';
import { CloseModal } from '../../sites/closemodal';

interface ProjectClaim {
  id: number;
  engineer_id: number;
  project_site_id: number;
  activity: string;
  survey_date: string;
  work_status?: string;
  site_latitude?: number;
  site_longitude?: number;
  engg_attendance_latitude?: number;
  engg_attendance_longitude?: number;
  engg_reporting_latitude?: number;
  engg_reporting_longitude?: number;
  site_distance_from_attendance?: number;
  site_distance_from_reporting?: number;
  final_shortest_distance?: number;
  engg_reporting_district?: string;
  district_same_as_site?: boolean;
  engg_within_state?: boolean;
  ta_slab_category?: string;
  kms_slab_category?: string;
  travel_mode?: string;
  submitted_site_distance?: number;
  submitted_travel_amount?: number;
  submitted_da_amount?: number;
  submitted_stay_amount?: number;
  submitted_stationery_amount?: number;
  submitted_other_amount1?: number;
  submitted_other_description1?: string;
  submitted_other_amount2?: number;
  submitted_other_description2?: string;
  submitted_engineer_total?: number;
  approved_site_distance?: number;
  approved_travel_amount?: number;
  approved_da_amount?: number;
  approved_stay_amount?: number;
  approved_stationery_amount?: number;
  approved_other_amount1?: number;
  approved_other_description1?: string;
  approved_other_amount2?: number;
  approved_other_description2?: string;
  approved_extra_amount?: number;
  approved_incentive_amount?: number;
  final_approved_total?: number;
  other_amount3?: number;
  other_description3?: string;
  terrain_type?: string;
  status: string;
  submitted_at?: string;
  approved_at?: string;
  approved_by?: number;
  rejection_reason?: string;
  bill_attachments?: string[];
  created_at: string;
  updated_at: string;
  rejected_at?: string;
  engineer: {
    id: number;
    first_name: string;
    last_name: string;
    association_type: string;
  };
  rejector?: {
    id: number;
    first_name: string;
    last_name: string;
    association_type: string;
  } | null;
  project_site: {
    id: number;
    site_id: string;
    site_name: string;
    created_at: string;
    site_latitude: number;
    site_longitude: number;
  };
  approver?: {
    id: number;
    first_name: string;
    last_name: string;
    association_type?: string;
  } | null;
}

interface ApprovalFormData {
  approved_site_distance?: number;
  approved_travel_amount?: number;
  approved_da_amount?: number;
  approved_stay_amount?: number;
  approved_stationery_amount?: number;
  approved_other_amount1?: number;
  approved_other_description1?: string;
  approved_other_amount2?: number;
  approved_other_description2?: string;
  approved_extra_amount?: number;
  approved_incentive_amount?: number;
  kms_slab_category?: string;
}

const ProjectClaimView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const [claim, setClaim] = useState<ProjectClaim | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [showApprovalDrawer, setShowApprovalDrawer] = useState<boolean>(false);
  const [showRejectDrawer, setShowRejectDrawer] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [approvalData, setApprovalData] = useState<ApprovalFormData>({});
  const [closeModalTarget, setCloseModalTarget] = useState<'approve' | 'reject' | null>(null);

  // Edit drawer
  const [showEditDrawer, setShowEditDrawer] = useState<boolean>(searchParams.get('edit') === 'true');
  const [editForm, setEditForm] = useState({
    ta_slab_category: '', travel_mode: '', submitted_site_distance: '',
    submitted_travel_amount: '', submitted_da_amount: '', submitted_stay_amount: '',
    submitted_stationery_amount: '', submitted_other_amount1: '', submitted_other_description1: '',
    submitted_other_amount2: '', submitted_other_description2: '', terrain_type: '',
  });
  const [editLoading, setEditLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState<any>({});
  const [touched, setTouched] = useState<any>({});

  const tabs = [
    { id: 'details', label: 'Claim Details', icon: FiInfo },
    { id: 'amounts', label: 'Amounts', icon: FaRupeeSign },
    { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'attachments', label: 'Attachments', icon: FiFileText },
    // { id: 'additional', label: 'Additional Info', icon: FiActivity },  
    { id: 'logs', label: 'Logs', icon: LuActivity },
  ];

  useEffect(() => {
    fetchClaimData();
  }, [id]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !actionLoading) {
        if (showApprovalDrawer) {
          requestCloseDrawer('approve');
        } else if (showRejectDrawer) {
          requestCloseDrawer('reject');
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showApprovalDrawer, showRejectDrawer, actionLoading, closeModalTarget]);

  useEffect(() => {
    if (!showApprovalDrawer && closeModalTarget === 'approve') {
      setCloseModalTarget(null);
    }
    if (!showRejectDrawer && closeModalTarget === 'reject') {
      setCloseModalTarget(null);
    }
  }, [showApprovalDrawer, showRejectDrawer, closeModalTarget]);

  const requestCloseDrawer = (target: 'approve' | 'reject') => {
    if (!actionLoading) {
      setCloseModalTarget(target);
    }
  };

  const confirmCloseDrawer = () => {
    if (closeModalTarget === 'approve') {
      setShowApprovalDrawer(false);
    } else if (closeModalTarget === 'reject') {
      setShowRejectDrawer(false);
    }
    setCloseModalTarget(null);
  };

  const fetchClaimData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/claims/project/${id}`);
      const data = response.data.data;
      setClaim(data);

      // Populate edit form
      if (data) {
        setEditForm({
          ta_slab_category: data.ta_slab_category || '',
          travel_mode: data.travel_mode || '',
          submitted_site_distance: data.submitted_site_distance != null ? String(data.submitted_site_distance) : '',
          submitted_travel_amount: data.submitted_travel_amount != null ? String(data.submitted_travel_amount) : '',
          submitted_da_amount: data.submitted_da_amount != null ? String(data.submitted_da_amount) : '',
          submitted_stay_amount: data.submitted_stay_amount != null ? String(data.submitted_stay_amount) : '',
          submitted_stationery_amount: data.submitted_stationery_amount != null ? String(data.submitted_stationery_amount) : '',
          submitted_other_amount1: data.submitted_other_amount1 != null ? String(data.submitted_other_amount1) : '',
          submitted_other_description1: data.submitted_other_description1 || '',
          submitted_other_amount2: data.submitted_other_amount2 != null ? String(data.submitted_other_amount2) : '',
          submitted_other_description2: data.submitted_other_description2 || '',
          terrain_type: data.terrain_type || '',
        });
      }

      // Initialize approval data with submitted amounts
      if (data) {
        setApprovalData({
          approved_site_distance: data.submitted_site_distance,
          approved_travel_amount: data.submitted_travel_amount,
          approved_da_amount: response.data.data.submitted_da_amount,
          approved_stay_amount: response.data.data.submitted_stay_amount,
          approved_stationery_amount: response.data.data.submitted_stationery_amount,
          approved_other_amount1: response.data.data.submitted_other_amount1,
          approved_other_description1: response.data.data.submitted_other_description1,
          approved_other_amount2: response.data.data.submitted_other_amount2,
          approved_other_description2: response.data.data.submitted_other_description2,
          approved_extra_amount: response.data.data.approved_extra_amount || 0,
          approved_incentive_amount: response.data.data.approved_incentive_amount || 0,
          kms_slab_category: response.data.data.kms_slab_category || response.data.data.ta_slab_category || ''
        });
      }
    } catch (error) {
      console.error('Error fetching claim:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (!claim) return;
    try {
      setEditLoading(true);
      await axiosInstance.put(`/claims/project/${id}/update`, {
        ta_slab_category: editForm.ta_slab_category,
        travel_mode: editForm.travel_mode,
        submitted_site_distance: editForm.submitted_site_distance !== '' ? Number(editForm.submitted_site_distance) : undefined,
        submitted_travel_amount: editForm.submitted_travel_amount !== '' ? Number(editForm.submitted_travel_amount) : 0,
        submitted_da_amount: editForm.submitted_da_amount !== '' ? Number(editForm.submitted_da_amount) : 0,
        submitted_stay_amount: editForm.submitted_stay_amount !== '' ? Number(editForm.submitted_stay_amount) : 0,
        submitted_stationery_amount: editForm.submitted_stationery_amount !== '' ? Number(editForm.submitted_stationery_amount) : 0,
        submitted_other_amount1: editForm.submitted_other_amount1 !== '' ? Number(editForm.submitted_other_amount1) : 0,
        submitted_other_description1: editForm.submitted_other_description1,
        submitted_other_amount2: editForm.submitted_other_amount2 !== '' ? Number(editForm.submitted_other_amount2) : 0,
        submitted_other_description2: editForm.submitted_other_description2,
        terrain_type: editForm.terrain_type,
      });
      await fetchClaimData();
      setShowEditDrawer(false);
      navigate(`/claims/project/${id}`, { replace: true });
    } catch (error) {
      console.error('Error updating claim:', error);
    } finally {
      setEditLoading(false);
    }
  };

  const validateAmount = (value: number | undefined, maxValue: number | undefined, fieldName: string): string => {
    if (value === undefined || value === null) {
      return '';
    }

    const valueStr = value.toString();

    if (!validator.isNumeric(valueStr, { no_symbols: false })) {
      return `${fieldName} must be a valid number`;
    }

    if (!validator.isFloat(valueStr, { min: 0 })) {
      return `${fieldName} must be zero or greater`;
    }

    if (maxValue !== undefined && value > maxValue) {
      return `${fieldName} cannot exceed submitted amount`;
    }

    return '';
  };

  const validateDistance = (value: number | undefined, maxValue: number | undefined): string => {
    if (value === undefined || value === null) {
      return '';
    }

    const valueStr = value.toString();

    if (!validator.isNumeric(valueStr, { no_symbols: false })) {
      return 'Distance must be a valid number';
    }

    if (!validator.isFloat(valueStr, { min: 0 })) {
      return 'Distance must be zero or greater';
    }

    if (maxValue !== undefined && value > maxValue) {
      return 'Distance cannot exceed submitted distance';
    }

    return '';
  };

  const validateRejectionReason = (value: string): string => {
    if (!value || validator.isEmpty(value.trim())) {
      return 'Rejection reason is required';
    }

    if (!validator.isLength(value.trim(), { min: 10, max: 500 })) {
      return 'Rejection reason must be between 10 and 500 characters';
    }

    if (!validator.matches(value, /^[a-zA-Z0-9\s.,!?'-]+$/)) {
      return 'Rejection reason contains invalid characters';
    }

    return '';
  };

  const validateApprovalForm = (): boolean => {
    const newErrors: any = {};

    // Validate distance
    const distanceError = validateDistance(
      approvalData.approved_site_distance,
      claim?.submitted_site_distance
    );
    if (distanceError) newErrors.approved_site_distance = distanceError;

    // Validate travel amount
    const travelError = validateAmount(
      approvalData.approved_travel_amount,
      claim?.submitted_travel_amount,
      'Travel amount'
    );
    if (travelError) newErrors.approved_travel_amount = travelError;

    // Only validate inhouse-specific fields if association_type is inhouse
    if (claim?.engineer.association_type === 'inhouse') {
      const daError = validateAmount(
        approvalData.approved_da_amount,
        claim?.submitted_da_amount,
        'DA amount'
      );
      if (daError) newErrors.approved_da_amount = daError;

      const stayError = validateAmount(
        approvalData.approved_stay_amount,
        claim?.submitted_stay_amount,
        'Stay amount'
      );
      if (stayError) newErrors.approved_stay_amount = stayError;

      const stationeryError = validateAmount(
        approvalData.approved_stationery_amount,
        claim?.submitted_stationery_amount,
        'Stationery amount'
      );
      if (stationeryError) newErrors.approved_stationery_amount = stationeryError;
    }

    // Validate extra and incentive amounts (they have no max, just need to be >= 0)
    if (approvalData.approved_extra_amount !== undefined) {
      const extraStr = approvalData.approved_extra_amount.toString();
      if (!validator.isNumeric(extraStr, { no_symbols: false }) || !validator.isFloat(extraStr, { min: 0 })) {
        newErrors.approved_extra_amount = 'Extra amount must be zero or greater';
      }
    }

    if (approvalData.approved_incentive_amount !== undefined) {
      const incentiveStr = approvalData.approved_incentive_amount.toString();
      if (!validator.isNumeric(incentiveStr, { no_symbols: false }) || !validator.isFloat(incentiveStr, { min: 0 })) {
        newErrors.approved_incentive_amount = 'Incentive amount must be zero or greater';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRejectForm = (): boolean => {
    const newErrors: any = {};

    const reasonError = validateRejectionReason(rejectionReason);
    if (reasonError) {
      newErrors.rejectionReason = reasonError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!validateApprovalForm()) return;

    try {
      setActionLoading(true);
      await axiosInstance.put('/claims/project/approve-reject', {
        id: Number(id),
        status: 'approved',
        ...approvalData
      });
      await fetchClaimData();
      setShowApprovalDrawer(false);
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error approving claim:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!validateRejectForm()) return;

    try {
      setActionLoading(true);
      await axiosInstance.put('/claims/project/approve-reject', {
        id: Number(id),
        status: 'rejected',
        rejection_reason: rejectionReason.trim()
      });
      await fetchClaimData();
      setShowRejectDrawer(false);
      setRejectionReason('');
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error rejecting claim:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'pending': { icon: FiClock, bgColor: 'bg-gray-100', textColor: 'text-gray-800', label: 'Pending' },
      'submitted': { icon: FiClock, bgColor: 'bg-blue-100', textColor: 'text-blue-800', label: 'Submitted' },
      'approved': { icon: FiCheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'Approved' },
      'rejected': { icon: FiAlertCircle, bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'Rejected' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const calculateApprovedTotal = () => {
    return (
      (approvalData.approved_travel_amount || 0) +
      (approvalData.approved_da_amount || 0) +
      (approvalData.approved_stay_amount || 0) +
      (approvalData.approved_stationery_amount || 0) +
      (approvalData.approved_other_amount1 || 0) +
      (approvalData.approved_other_amount2 || 0) +
      (approvalData.approved_extra_amount || 0) +
      (approvalData.approved_incentive_amount || 0)
    );
  };

  const renderDetailsTab = () => (
    <div className="space-y-4">
      {/* Basic Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* <div>
            <label className="text-xs font-medium text-gray-500">Claim ID</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.id}</p>
          </div> */}
          <div>
            <label className="text-xs font-medium text-gray-500">Site ID</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.project_site.site_id}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Site Name</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.project_site.site_name}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Engineer</label>
            <p className="text-sm text-gray-900 mt-1">
              {claim?.engineer.first_name} {claim?.engineer.last_name}
            </p>
          </div>
          {/* <div>
            <label className="text-xs font-medium text-gray-500">Engineer ID</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.engineer_id}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Project Site ID</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.project_site_id}</p>
          </div> */}
          <div>
            <label className="text-xs font-medium text-gray-500">Association Type</label>
            <p className="text-sm text-gray-900 mt-1">
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${claim?.engineer.association_type === 'inhouse' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                }`}>
                {claim?.engineer.association_type}
              </span>
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Travel Mode</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.travel_mode || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Terrain Type</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.terrain_type || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Kms Slab Category (Submitted)</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.ta_slab_category || 'N/A'}</p>
          </div>
          {claim?.status === 'approved' && claim?.kms_slab_category && (
            <div>
              <label className="text-xs font-medium text-gray-500">Kms Slab Category (Approved)</label>
              <p className="text-sm text-gray-900 mt-1">{claim.kms_slab_category}</p>
            </div>
          )}
        </div>
      </div>

      {/* Distance Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Distance Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Distance from Attendance</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.site_distance_from_attendance || '0'} km</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Distance from Reporting</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.site_distance_from_reporting || '0'} km</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Final Shortest Distance</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.final_shortest_distance || '0'} km</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Submitted Site Distance</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.submitted_site_distance || '0'} km</p>
          </div>
          {claim?.status !== 'rejected' && (
            <div>
              <label className="text-xs font-medium text-gray-500">Approved Site Distance</label>
              <p className="text-sm text-gray-900 mt-1">{claim?.approved_site_distance || '0'} km</p>
            </div>
          )}
        </div>
      </div>

      {/* Location Validation */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Location Validation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Engineer Reporting District</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.engg_reporting_district || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">District Same as Site</label>
            <p className="text-sm text-gray-900 mt-1">
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${claim?.district_same_as_site ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                {claim?.district_same_as_site ? 'Yes' : 'No'}
              </span>
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Engineer Within State</label>
            <p className="text-sm text-gray-900 mt-1">
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${claim?.engg_within_state ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                {claim?.engg_within_state ? 'Yes' : 'No'}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Status Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Status Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Current Status</label>
            <p className="text-sm text-gray-900 mt-1">{getStatusBadge(claim?.status || 'pending')}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Submitted At</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.submitted_at ? formatDate(claim.submitted_at) : 'Not submitted'}</p>
          </div>
          {claim?.status === 'approved' && (
            <div>
              <label className="text-xs font-medium text-gray-500">Approved At</label>
              <p className="text-sm text-gray-900 mt-1">{claim?.approved_at ? formatDate(claim.approved_at) : 'Not approved'}</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500">Created At</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.created_at ? formatDate(claim.created_at) : 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Updated At</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.updated_at ? formatDate(claim.updated_at) : 'N/A'}</p>
          </div>
          {claim?.approved_by && claim?.status === 'approved' && claim?.approver && (
            <div>
              <label className="text-xs font-medium text-gray-500">Approved By</label>
              <p className="text-sm text-gray-900 mt-1 font-semibold">
                {`${claim.approver.first_name} ${claim.approver.last_name}`}
              </p>
              {claim?.approver?.association_type && (
                <p className="text-xs text-gray-600 mt-1 capitalize">{claim.approver.association_type}</p>
              )}
            </div>
          )}
          {claim?.status === 'rejected' && (
            <>
              <div>
                <label className="text-xs font-medium text-gray-500">Rejected At</label>
                <p className="text-sm text-gray-900 mt-1">{claim?.rejected_at ? formatDate(claim.rejected_at as string) : 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Rejected By</label>
                <p className="text-sm text-gray-900 mt-1 font-semibold">
                  {claim?.rejector ? `${claim.rejector.first_name} ${claim.rejector.last_name}` : 'N/A'}
                </p>
                {claim?.rejector?.association_type && (
                  <p className="text-xs text-gray-600 mt-1.capitalize">{claim.rejector.association_type}</p>
                )}
              </div>
            </>
          )}
          {claim?.rejection_reason && (
            <div className="md:col-span-3">
              <label className="text-xs font-medium text-gray-500">Rejection Reason</label>
              <p className="text-sm text-red-900 mt-1 bg-red-50 p-3 rounded">{claim.rejection_reason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAmountsTab = () => (
    <div className="space-y-4">
      {/* Submitted Amounts */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Submitted Amounts</h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Travel Amount</span>
            <span className="text-sm font-medium text-gray-900">{formatCurrency(claim?.submitted_travel_amount)}</span>
          </div>
          <>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">DA Amount</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim?.submitted_da_amount)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Stay Amount</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim?.submitted_stay_amount)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Stationery Amount</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim?.submitted_stationery_amount)}</span>
            </div>
          </>

          {claim?.submitted_other_amount1 && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{claim?.submitted_other_description1 || 'Other Amount 1'}</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim?.submitted_other_amount1)}</span>
            </div>
          )}
          {claim?.submitted_other_amount2 && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{claim?.submitted_other_description2 || 'Other Amount 2'}</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim?.submitted_other_amount2)}</span>
            </div>
          )}
          <div className="flex justify-between py-3 border-t-2 border-gray-200 mt-2">
            <span className="text-base font-semibold text-gray-900">Total Submitted</span>
            <span className="text-base font-bold text-blue-900">{formatCurrency(claim?.submitted_engineer_total)}</span>
          </div>
        </div>
      </div>

      {/* Approved Amounts */}
      {(claim?.status === 'approved' || claim?.status === 'submitted') && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            {claim?.status === 'approved' ? 'Approved Amounts' : 'Approval Preview'}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Travel Amount</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(claim?.approved_travel_amount || approvalData.approved_travel_amount)}
              </span>
            </div>
            {claim?.engineer.association_type === 'inhouse' && (
              <>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">DA Amount</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(claim?.approved_da_amount || approvalData.approved_da_amount)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Stay Amount</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(claim?.approved_stay_amount || approvalData.approved_stay_amount)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Stationery Amount</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(claim?.approved_stationery_amount || approvalData.approved_stationery_amount)}
                  </span>
                </div>
              </>
            )}
            {(claim?.approved_other_amount1 || approvalData.approved_other_amount1) && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">
                  {claim?.approved_other_description1 || approvalData.approved_other_description1 || 'Other Amount 1'}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(claim?.approved_other_amount1 || approvalData.approved_other_amount1)}
                </span>
              </div>
            )}
            {(claim?.approved_other_amount2 || approvalData.approved_other_amount2) && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">
                  {claim?.approved_other_description2 || approvalData.approved_other_description2 || 'Other Amount 2'}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(claim?.approved_other_amount2 || approvalData.approved_other_amount2)}
                </span>
              </div>
            )}
            {(claim?.approved_extra_amount || approvalData.approved_extra_amount) ? (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Extra Amount</span>
                <span className="text-sm font-medium text-green-900">
                  {formatCurrency(claim?.approved_extra_amount || approvalData.approved_extra_amount)}
                </span>
              </div>
            ) : null}
            {(claim?.approved_incentive_amount || approvalData.approved_incentive_amount) ? (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Incentive Amount</span>
                <span className="text-sm font-medium text-green-900">
                  {formatCurrency(claim?.approved_incentive_amount || approvalData.approved_incentive_amount)}
                </span>
              </div>
            ) : null}
            <div className="flex justify-between py-3 border-t-2 border-gray-200 mt-2">
              <span className="text-base font-semibold text-gray-900">
                {claim?.status === 'approved' ? 'Total Approved' : 'Total Preview'}
              </span>
              <span className="text-base font-bold text-green-900">
                {formatCurrency(claim?.final_approved_total || calculateApprovedTotal())}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderLocationTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Location Coordinates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {claim && (
            <div className="bg-gray-50 p-3 rounded">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <FiMapPin className="w-3 h-3" />
                Site Location
              </label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{claim.project_site.site_latitude}, {claim.project_site.site_longitude}</p>
            </div>
          )}
          {/* {claim?.engg_attendance_latitude && claim?.engg_attendance_longitude && (
            <div className="bg-gray-50 p-3 rounded">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <FiUser className="w-3 h-3" />
                Attendance Location
              </label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{claim.engg_attendance_latitude}, {claim.engg_attendance_longitude}</p>
            </div>
          )}
          {claim?.engg_reporting_latitude && claim?.engg_reporting_longitude && (
            <div className="bg-gray-50 p-3 rounded">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <FiNavigation className="w-3 h-3" />
                Reporting Location
              </label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{claim.engg_reporting_latitude}, {claim.engg_reporting_longitude}</p>
            </div>
          )}
          {claim?.engg_reporting_district && (
            <div className="bg-gray-50 p-3 rounded">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <FiGlobe className="w-3 h-3" />
                Reporting District
              </label>
              <p className="text-sm text-gray-900 mt-1">{claim.engg_reporting_district}</p>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );

  const renderAttachmentsTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Bill Attachments</h3>
        {claim?.bill_attachments && claim.bill_attachments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {claim.bill_attachments.map((attachment, index) => (
              <div key={index} className="border border-gray-200 rounded p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <FiFileText className="w-8 h-8 text-blue-800" />
                  <a
                    href={attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-800 hover:text-blue-800"
                  >
                    <FaDownload className="w-4 h-4" />
                  </a>
                </div>
                <p className="text-xs text-gray-600 truncate">Attachment {index + 1}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FiFileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No attachments found</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderAdditionalInfoTab = () => (
    <div className="space-y-4">
      {/* Additional Amounts */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Additional Amount Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {claim?.other_amount3 && (
            <div>
              <label className="text-xs font-medium text-gray-500">Other Amount 3</label>
              <p className="text-sm text-gray-900 mt-1">{formatCurrency(claim.other_amount3)}</p>
              {claim.other_description3 && (
                <p className="text-xs text-gray-500 mt-1">Description: {claim.other_description3}</p>
              )}
            </div>
          )}
          {claim?.status === 'approved' && claim?.approved_extra_amount && (
            <div>
              <label className="text-xs font-medium text-gray-500">Approved Extra Amount</label>
              <p className="text-sm text-green-900 mt-1 font-medium">{formatCurrency(claim.approved_extra_amount)}</p>
            </div>
          )}
          {claim?.status === 'approved' && claim?.approved_incentive_amount && (
            <div>
              <label className="text-xs font-medium text-gray-500">Approved Incentive Amount</label>
              <p className="text-sm text-green-900 mt-1 font-medium">{formatCurrency(claim.approved_incentive_amount)}</p>
            </div>
          )}
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">System Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Claim ID</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.id}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Engineer ID</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.engineer_id}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Project Site ID</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.project_site_id}</p>
          </div>
          {claim?.status === 'approved' && (
            <div>
              <label className="text-xs font-medium text-gray-500">Approved By User ID</label>
              <p className="text-sm text-gray-900 mt-1">{claim?.approved_by || 'Not approved'}</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500">Created At</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.created_at ? formatDate(claim.created_at) : 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Updated At</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.updated_at ? formatDate(claim.updated_at) : 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Validation Flags */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Validation Flags</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded">
            <label className="text-xs font-medium text-gray-500">District Match</label>
            <p className={`text-sm font-medium mt-1 ${claim?.district_same_as_site ? 'text-green-600' : 'text-red-600'}`}>
              {claim?.district_same_as_site ? 'MATCHED' : 'NOT MATCHED'}
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <label className="text-xs font-medium text-gray-500">Within State</label>
            <p className={`text-sm font-medium mt-1 ${claim?.engg_within_state ? 'text-green-600' : 'text-red-600'}`}>
              {claim?.engg_within_state ? 'WITHIN STATE' : 'OUT OF STATE'}
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <label className="text-xs font-medium text-gray-500">Technical Associate Category</label>
            <p className="text-sm font-medium text-blue-600 mt-1">{claim?.ta_slab_category || 'N/A'}</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <label className="text-xs font-medium text-gray-500">Association</label>
            <p className={`text-sm font-medium mt-1 ${claim?.engineer.association_type === 'inhouse' ? 'text-purple-600' : 'text-orange-600'}`}>
              {claim?.engineer.association_type?.toUpperCase()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading claim details...</p>
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Claim Not Found</h2>
          <p className="text-gray-600 mb-4">The claim you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/claims/project')}
            className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900"
          >
            Back to Claims
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Project Claim #{claim.id}</h1>
              <p className="text-sm text-gray-600 mt-1">{claim.project_site.site_name} ({claim.project_site.site_id})</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-2">
                <div className="flex items-center gap-1">
                  <FiCalendar size={12} />
                  Created: {formatDate(claim.created_at)}
                </div>
                <div className="flex items-center gap-1">
                  <FiUser size={12} />
                  Engineer: {claim.engineer.first_name + ' ' + claim.engineer.last_name || ''}
                </div>
                <div className="flex items-center gap-1">
                  <FiActivity size={12} />
                  Association: {claim.engineer.association_type}
                </div>
                {claim.status === 'rejected' && claim.rejected_at && (
                  <div className="flex items-center gap-1 text-red-600">
                    <FiCalendar size={12} />
                    Rejected: {formatDate(claim.rejected_at as string)}
                  </div>
                )}
                {claim.status === 'rejected' && claim.rejector && (
                  <div className="flex items-center gap-1 text-red-600">
                    <FiUser size={12} />
                    {claim.rejector.first_name} {claim.rejector.last_name}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(claim.status)}
              {claim.status === 'submitted' && (
                <>
                  <button
                    onClick={() => setShowApprovalDrawer(true)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    <FiCheck className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => setShowRejectDrawer(true)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    <FiX className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                      ? 'border-blue-800 text-blue-800'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && renderDetailsTab()}
        {activeTab === 'amounts' && renderAmountsTab()}
        {activeTab === 'location' && renderLocationTab()}
        {activeTab === 'attachments' && renderAttachmentsTab()}
        {activeTab === 'additional' && renderAdditionalInfoTab()}
        {activeTab === 'logs' && <EntityLogs entity="project_claim" entityId={id as string} />}

        {/* Approval Drawer */}
        {showApprovalDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => requestCloseDrawer('approve') : undefined}
            />

            <div className="overflow-y-auto fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Approve Claim</h3>
                  <p className="text-sm text-gray-600 mt-1">Review and adjust claim amounts</p>
                </div>
                <button
                  onClick={() => requestCloseDrawer('approve')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {/* Distance Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site Distance (km)
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_site_distance || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_site_distance: value });
                        setTouched({ ...touched, approved_site_distance: true });
                        if (errors.approved_site_distance) {
                          setErrors({ ...errors, approved_site_distance: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_site_distance: true });
                        const error = validateDistance(
                          approvalData.approved_site_distance,
                          claim?.submitted_site_distance
                        );
                        if (error) {
                          setErrors({ ...errors, approved_site_distance: error });
                        }
                      }}
                      max={claim?.submitted_site_distance || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_site_distance && touched.approved_site_distance
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {claim?.submitted_site_distance} km</p>
                    {errors.approved_site_distance && touched.approved_site_distance && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_site_distance}</p>
                    )}
                  </div>

                  {/* Travel Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Travel Amount
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_travel_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_travel_amount: value });
                        setTouched({ ...touched, approved_travel_amount: true });
                        if (errors.approved_travel_amount) {
                          setErrors({ ...errors, approved_travel_amount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_travel_amount: true });
                        const error = validateAmount(
                          approvalData.approved_travel_amount,
                          claim?.submitted_travel_amount,
                          'Travel amount'
                        );
                        if (error) {
                          setErrors({ ...errors, approved_travel_amount: error });
                        }
                      }}
                      max={claim?.submitted_travel_amount || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_travel_amount && touched.approved_travel_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {formatCurrency(claim?.submitted_travel_amount)}</p>
                    {errors.approved_travel_amount && touched.approved_travel_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_travel_amount}</p>
                    )}
                  </div>

                  {/* DA Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      DA Amount
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_da_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_da_amount: value });
                        setTouched({ ...touched, approved_da_amount: true });
                        if (errors.approved_da_amount) {
                          setErrors({ ...errors, approved_da_amount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_da_amount: true });
                        const error = validateAmount(
                          approvalData.approved_da_amount,
                          claim?.submitted_da_amount,
                          'DA amount'
                        );
                        if (error) {
                          setErrors({ ...errors, approved_da_amount: error });
                        }
                      }}
                      max={claim?.submitted_da_amount || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_da_amount && touched.approved_da_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {formatCurrency(claim?.submitted_da_amount)}</p>
                    {errors.approved_da_amount && touched.approved_da_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_da_amount}</p>
                    )}
                  </div>

                  {/* Stay Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stay Amount
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_stay_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_stay_amount: value });
                        setTouched({ ...touched, approved_stay_amount: true });
                        if (errors.approved_stay_amount) {
                          setErrors({ ...errors, approved_stay_amount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_stay_amount: true });
                        const error = validateAmount(
                          approvalData.approved_stay_amount,
                          claim?.submitted_stay_amount,
                          'Stay amount'
                        );
                        if (error) {
                          setErrors({ ...errors, approved_stay_amount: error });
                        }
                      }}
                      max={claim?.submitted_stay_amount || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_stay_amount && touched.approved_stay_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {formatCurrency(claim?.submitted_stay_amount)}</p>
                    {errors.approved_stay_amount && touched.approved_stay_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_stay_amount}</p>
                    )}
                  </div>

                  {/* Stationery Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stationery Amount
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_stationery_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_stationery_amount: value });
                        setTouched({ ...touched, approved_stationery_amount: true });
                        if (errors.approved_stationery_amount) {
                          setErrors({ ...errors, approved_stationery_amount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_stationery_amount: true });
                        const error = validateAmount(
                          approvalData.approved_stationery_amount,
                          claim?.submitted_stationery_amount,
                          'Stationery amount'
                        );
                        if (error) {
                          setErrors({ ...errors, approved_stationery_amount: error });
                        }
                      }}
                      max={claim?.submitted_stationery_amount || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_stationery_amount && touched.approved_stationery_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {formatCurrency(claim?.submitted_stationery_amount)}</p>
                    {errors.approved_stationery_amount && touched.approved_stationery_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_stationery_amount}</p>
                    )}
                  </div>

                  {/* Extra Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Extra Amount (Optional)
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_extra_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_extra_amount: value });
                        setTouched({ ...touched, approved_extra_amount: true });
                        if (errors.approved_extra_amount) {
                          setErrors({ ...errors, approved_extra_amount: '' });
                        }
                      }}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_extra_amount && touched.approved_extra_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Additional amount beyond submitted (no limit)</p>
                    {errors.approved_extra_amount && touched.approved_extra_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_extra_amount}</p>
                    )}
                  </div>

                  {/* Incentive Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Incentive Amount (Optional)
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_incentive_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_incentive_amount: value });
                        setTouched({ ...touched, approved_incentive_amount: true });
                        if (errors.approved_incentive_amount) {
                          setErrors({ ...errors, approved_incentive_amount: '' });
                        }
                      }}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_incentive_amount && touched.approved_incentive_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Performance incentive (no limit)</p>
                    {errors.approved_incentive_amount && touched.approved_incentive_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_incentive_amount}</p>
                    )}
                  </div>

                  {/* KMS Slab Category Approved */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      KMS Slab Category Approved
                    </label>
                    <select
                      value={approvalData.kms_slab_category || ''}
                      onChange={(e) => {
                        setApprovalData({ ...approvalData, kms_slab_category: e.target.value });
                        setTouched({ ...touched, kms_slab_category: true });
                      }}
                      disabled={actionLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select KMS Slab Category</option>
                      <option value="0-100">0-100 KMS</option>
                      <option value="100-200">100-200 KMS</option>
                      <option value="200-300">200-300 KMS</option>
                      <option value="300-400">300-400 KMS</option>
                      <option value="400-500">400-500 KMS</option>
                      <option value="500+">500+ KMS</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Submitted: {claim?.ta_slab_category || 'N/A'}</p>
                  </div>

                  {/* Total Summary */}
                  <div className="bg-gray-50 p-4 rounded">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Total Approved Amount:</span>
                      <span className="text-lg font-bold text-green-900">{formatCurrency(calculateApprovedTotal())}</span>
                    </div>
                  </div>

                  {/* Guidelines */}
                  <div className="bg-blue-50 border border-blue-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiInfo className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Approval Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Regular amounts can only be equal to or less than submitted</li>
                          <li>Extra and incentive amounts can be any positive value</li>
                          <li>All amounts must be zero or greater</li>
                          <li>Verify supporting documents before approval</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => requestCloseDrawer('approve')}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Approving...' : 'Approve Claim'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Reject Drawer */}
        {showRejectDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => requestCloseDrawer('reject') : undefined}
            />

            <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Reject Claim</h3>
                  <p className="text-sm text-gray-600 mt-1">Provide a reason for rejection</p>
                </div>
                <button
                  onClick={() => requestCloseDrawer('reject')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => {
                        setRejectionReason(e.target.value);
                        setTouched({ ...touched, rejectionReason: true });
                        if (errors.rejectionReason) {
                          setErrors({ ...errors, rejectionReason: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, rejectionReason: true });
                        const error = validateRejectionReason(rejectionReason);
                        if (error) {
                          setErrors({ ...errors, rejectionReason: error });
                        }
                      }}
                      rows={4}
                      disabled={actionLoading}
                      placeholder="Please provide a detailed reason for rejection (minimum 10 characters)..."
                      maxLength={500}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-red-500 ${errors.rejectionReason && touched.rejectionReason
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {rejectionReason.length}/500 characters (minimum 10)
                    </p>
                    {errors.rejectionReason && touched.rejectionReason && (
                      <p className="mt-1 text-sm text-red-600">{errors.rejectionReason}</p>
                    )}
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiAlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p className="font-medium mb-1">Rejection Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Provide a clear and specific reason</li>
                          <li>Be professional and constructive</li>
                          <li>Use only alphanumeric characters and basic punctuation</li>
                          <li>This action cannot be undone</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => requestCloseDrawer('reject')}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={actionLoading || !rejectionReason.trim()}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Rejecting...' : 'Reject Claim'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <CloseModal
        isOpen={closeModalTarget !== null}
        onClose={() => setCloseModalTarget(null)}
        onConfirm={confirmCloseDrawer}
      />

      {/* ── Edit Submitted Project Claim Drawer ── */}
      {showEditDrawer && claim && claim.status === 'submitted' && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowEditDrawer(false)} />
          <div className="fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FiEdit className="text-orange-600" size={18} /> Edit Project Claim
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Only editable while status is "Submitted"</p>
              </div>
              <button onClick={() => setShowEditDrawer(false)} className="p-1.5 hover:bg-gray-200 rounded-lg">
                <FiX size={18} className="text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Slab Category</label>
                  <input type="text" value={editForm.ta_slab_category}
                    onChange={e => setEditForm(f => ({ ...f, ta_slab_category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Travel Mode</label>
                  <select value={editForm.travel_mode} onChange={e => setEditForm(f => ({ ...f, travel_mode: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                    <option value="">Select...</option>
                    <option value="two_wheeler">Two Wheeler</option>
                    <option value="four_wheeler">Four Wheeler</option>
                    <option value="bus">Bus</option>
                    <option value="train">Train</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Site Distance (km)</label>
                  <input type="number" min="0" step="0.1" value={editForm.submitted_site_distance}
                    onChange={e => setEditForm(f => ({ ...f, submitted_site_distance: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Terrain Type</label>
                  <input type="text" value={editForm.terrain_type}
                    onChange={e => setEditForm(f => ({ ...f, terrain_type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'submitted_travel_amount', label: 'Travel Amount (₹)' },
                  { key: 'submitted_da_amount', label: 'DA Amount (₹)' },
                  { key: 'submitted_stay_amount', label: 'Stay Amount (₹)' },
                  { key: 'submitted_stationery_amount', label: 'Stationery Amount (₹)' },
                  { key: 'submitted_other_amount1', label: 'Other Amount 1 (₹)' },
                  { key: 'submitted_other_amount2', label: 'Other Amount 2 (₹)' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
                    <input type="number" min="0" value={(editForm as any)[key]}
                      onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Other Description 1</label>
                  <input type="text" value={editForm.submitted_other_description1}
                    onChange={e => setEditForm(f => ({ ...f, submitted_other_description1: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Other Description 2</label>
                  <input type="text" value={editForm.submitted_other_description2}
                    onChange={e => setEditForm(f => ({ ...f, submitted_other_description2: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              {/* Total preview */}
              <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-sm font-semibold text-gray-900 flex justify-between">
                <span>New Submitted Total</span>
                <span>₹{(
                  (Number(editForm.submitted_travel_amount) || 0) +
                  (Number(editForm.submitted_da_amount) || 0) +
                  (Number(editForm.submitted_stay_amount) || 0) +
                  (Number(editForm.submitted_stationery_amount) || 0) +
                  (Number(editForm.submitted_other_amount1) || 0) +
                  (Number(editForm.submitted_other_amount2) || 0)
                ).toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setShowEditDrawer(false)} disabled={editLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={editLoading}
                className="px-5 py-2 text-sm font-medium text-white bg-orange-700 rounded-lg hover:bg-orange-800 disabled:opacity-50 flex items-center gap-2">
                {editLoading
                  ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Saving...</>
                  : <><FiCheck size={14} />Save Changes</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectClaimView;